import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Avatar,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import api from "../utils/axiosInstance";
import imageCompression from "browser-image-compression";
import { PRODUCT_CATEGORIES } from "../utils/productCategories";

const ProductsManager = ({ shopSlug }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [form, setForm] = useState({
    name: "",
    price: "",
    description: "",
    stock: "",
    category: "",
    subcategory: "",
    images: [],
    existingImages: [],
  });

  const [uploadProgress, setUploadProgress] = useState(0);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [previewImage, setPreviewImage] = useState(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  /* ==================== FETCH PRODUCTS ==================== */

  useEffect(() => {
    if (shopSlug) {
      fetchProducts();
    }
  }, [shopSlug]);

  const normalizeProduct = (p) => ({
    ...p,
    images:
      p.images && Array.isArray(p.images)
        ? p.images
        : p.main_image
        ? [{ url: p.main_image }]
        : [],
  });

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/shops/${shopSlug}/products`);

      const rawProducts = Array.isArray(res.data)
        ? res.data
        : res.data.products || [];

      const formattedProducts = rawProducts.map(normalizeProduct);

      setProducts(formattedProducts);
    } catch (err) {
      console.error("Failed to fetch products:", err);

      setToast({
        open: true,
        message: "Failed to fetch products",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ==================== FETCH PRODUCT IMAGES ==================== */

  const fetchProductImages = async (productId) => {
    try {
      const res = await api.get(
        `/shops/${shopSlug}/products/${productId}/images`
      );

      return Array.isArray(res.data)
        ? res.data
        : res.data.images || [];
    } catch (err) {
      console.error("Failed to fetch product images:", err);
      return [];
    }
  };

  /* ==================== OPEN FORM ==================== */

  const handleOpenForm = async (product = null) => {
    setSelectedProduct(product);

    if (product) {
      const existingImages = await fetchProductImages(product.id);

      setForm({
        name: product.name || "",
        price: product.price || "",
        description: product.description || "",
        stock: product.stock || "",
        category: product.category || "",
        subcategory: product.subcategory || "",
        images: [],
        existingImages,
      });
    } else {
      setForm({
        name: "",
        price: "",
        description: "",
        stock: "",
        category: "",
        subcategory: "",
        images: [],
        existingImages: [],
      });
    }

    setFormOpen(true);
  };

  /* ==================== SAVE PRODUCT ==================== */

  const handleSave = async () => {
    if (!form.name || !form.price) {
      setToast({
        open: true,
        message: "Name and price are required",
        severity: "warning",
      });

      return;
    }

    if (
      !selectedProduct &&
      (!form.images ||
        form.images.length < 1 ||
        form.images.length > 4)
    ) {
      setToast({
        open: true,
        message: "Please upload between 1 and 4 images",
        severity: "warning",
      });

      return;
    }

    try {
      const formData = new FormData();

      formData.append("name", form.name);
      formData.append("price", form.price);
      formData.append("description", form.description || "");
      formData.append("stock", form.stock || 0);
      formData.append("category", form.category || "");
      formData.append(
        "subcategory",
        form.subcategory || ""
      );

      /* ===== COMPRESS IMAGES ===== */

      if (form.images && form.images.length > 0) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1200,
          useWebWorker: true,
        };

        const compressedFiles = await Promise.all(
          Array.from(form.images).map(async (file) => {
            try {
              return await imageCompression(file, options);
            } catch (err) {
              console.error("Compression failed:", err);
              return file;
            }
          })
        );

        compressedFiles.forEach((file) => {
          formData.append("images", file);
        });
      }

      setUploadProgress(0);

      let res;

      if (selectedProduct) {
        /* ===== UPDATE PRODUCT ===== */

        res = await api.put(
          `/shops/${shopSlug}/products/${selectedProduct.id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },

            onUploadProgress: (e) => {
              if (!e.total) return;

              setUploadProgress(
                Math.round((e.loaded * 100) / e.total)
              );
            },
          }
        );

        const updatedProduct = normalizeProduct(
          res.data.product || res.data
        );

        setProducts((prev) =>
          prev.map((p) =>
            p.id === selectedProduct.id ? updatedProduct : p
          )
        );

        setToast({
          open: true,
          message: "Product updated",
          severity: "success",
        });
      } else {
        /* ===== CREATE PRODUCT ===== */

        res = await api.post(
          `/shops/${shopSlug}/products`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },

            onUploadProgress: (e) => {
              if (!e.total) return;

              setUploadProgress(
                Math.round((e.loaded * 100) / e.total)
              );
            },
          }
        );

        const newProduct = normalizeProduct(
          res.data.product || res.data
        );

        setProducts((prev) => [newProduct, ...prev]);

        setToast({
          open: true,
          message: "Product added",
          severity: "success",
        });
      }

      setFormOpen(false);

      setSelectedProduct(null);

      setForm({
        name: "",
        price: "",
        description: "",
        stock: "",
        category: "",
        subcategory: "",
        images: [],
        existingImages: [],
      });

      setUploadProgress(0);
    } catch (err) {
      console.error("Save failed:", err);

      console.error("Backend response:", err.response?.data);

      setToast({
        open: true,
        message:
          err.response?.data?.message ||
          "Failed to save product",
        severity: "error",
      });

      setUploadProgress(0);
    }
  };

  /* ==================== DELETE PRODUCT ==================== */

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete product "${product.name}"?`)) {
      return;
    }

    try {
      await api.delete(
        `/shops/${shopSlug}/products/${product.id}`
      );

      setProducts((prev) =>
        prev.filter((p) => p.id !== product.id)
      );

      setToast({
        open: true,
        message: "Product deleted",
        severity: "success",
      });
    } catch (err) {
      console.error("Delete failed:", err);

      setToast({
        open: true,
        message: "Failed to delete product",
        severity: "error",
      });
    }
  };

  /* ==================== DELETE IMAGE ==================== */

  const handleDeleteImage = async (imageId) => {
    if (!selectedProduct) return;

    try {
      await api.delete(
        `/shops/${shopSlug}/products/${selectedProduct.id}/images/${imageId}`
      );

      setForm((prev) => ({
        ...prev,
        existingImages: prev.existingImages.filter(
          (img) => img.id !== imageId
        ),
      }));

      setToast({
        open: true,
        message: "Image deleted",
        severity: "success",
      });
    } catch (err) {
      console.error("Delete image failed:", err);

      setToast({
        open: true,
        message: "Failed to delete image",
        severity: "error",
      });
    }
  };

  /* ==================== RENDER ==================== */

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
        >
          Add Product
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Images</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>{product.name}</TableCell>

                  <TableCell>
                    {product.category || "-"}

                    {product.subcategory
                      ? ` / ${product.subcategory}`
                      : ""}
                  </TableCell>

                  <TableCell>
                    Ksh {product.price}
                  </TableCell>

                  <TableCell>
                    {product.stock ?? 0}
                  </TableCell>

                  <TableCell>
                    <Box display="flex" gap={1}>
                      {product.images?.map((img, idx) => (
                        <Avatar
                          key={idx}
                          src={img.url}
                          variant="rounded"
                          sx={{
                            width: 40,
                            height: 40,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setPreviewImage(img.url)
                          }
                        />
                      ))}
                    </Box>
                  </TableCell>

                  <TableCell align="right">
                    <IconButton
                      onClick={() =>
                        handleOpenForm(product)
                      }
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      onClick={() =>
                        handleDelete(product)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ==================== DIALOG ==================== */}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullScreen={fullScreen}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedProduct
            ? "Edit Product"
            : "Add Product"}
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={form.name}
            onChange={(e) =>
              setForm({
                ...form,
                name: e.target.value,
              })
            }
          />

          <TextField
            select
            fullWidth
            margin="normal"
            label="Category"
            value={form.category}
            onChange={(e) =>
              setForm({
                ...form,
                category: e.target.value,
                subcategory: "",
              })
            }
          >
            {Object.keys(PRODUCT_CATEGORIES).map(
              (category) => (
                <MenuItem
                  key={category}
                  value={category}
                >
                  {category}
                </MenuItem>
              )
            )}
          </TextField>

          <TextField
            select
            fullWidth
            margin="normal"
            label="Subcategory"
            value={form.subcategory}
            onChange={(e) =>
              setForm({
                ...form,
                subcategory: e.target.value,
              })
            }
            disabled={!form.category}
          >
            {(PRODUCT_CATEGORIES[
              form.category
            ] || []).map((sub) => (
              <MenuItem
                key={sub}
                value={sub}
              >
                {sub}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            margin="normal"
            label="Price"
            type="number"
            value={form.price}
            onChange={(e) =>
              setForm({
                ...form,
                price: e.target.value,
              })
            }
          />

          <TextField
            fullWidth
            margin="normal"
            label="Stock"
            type="number"
            value={form.stock}
            onChange={(e) =>
              setForm({
                ...form,
                stock: e.target.value,
              })
            }
          />

          <TextField
            fullWidth
            margin="normal"
            label="Description"
            multiline
            rows={3}
            value={form.description}
            onChange={(e) =>
              setForm({
                ...form,
                description: e.target.value,
              })
            }
          />

          {form.existingImages?.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2">
                Existing Images
              </Typography>

              <Box
                display="flex"
                gap={1}
                overflow="auto"
                mt={1}
              >
                {form.existingImages.map((img) => (
                  <Box
                    key={img.id}
                    position="relative"
                  >
                    <Avatar
                      src={img.url}
                      variant="rounded"
                      sx={{
                        width: 60,
                        height: 60,
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setPreviewImage(img.url)
                      }
                    />

                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        bgcolor: "white",
                      }}
                      onClick={() =>
                        handleDeleteImage(img.id)
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Button
            variant="outlined"
            component="label"
            sx={{ mt: 2 }}
          >
            Upload Images (1–4)

            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={(e) =>
                setForm({
                  ...form,
                  images: e.target.files,
                })
              }
            />
          </Button>

          {form.images && form.images.length > 0 && (
            <Box
              mt={1}
              display="flex"
              gap={1}
              overflow="auto"
            >
              {Array.from(form.images).map(
                (file, idx) => (
                  <Avatar
                    key={idx}
                    src={URL.createObjectURL(file)}
                    variant="rounded"
                    sx={{
                      width: 60,
                      height: 60,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setPreviewImage(
                        URL.createObjectURL(file)
                      )
                    }
                  />
                )
              )}
            </Box>
          )}

          {uploadProgress > 0 &&
            uploadProgress < 100 && (
              <Box sx={{ width: "100%", mt: 3 }}>
                <Typography variant="caption">
                  Uploading: {uploadProgress}%
                </Typography>

                <Box
                  sx={{
                    background: "#eee",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${uploadProgress}%`,
                      height: 8,
                      backgroundColor: "green",
                      transition: "width 0.3s",
                    }}
                  />
                </Box>
              </Box>
            )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setFormOpen(false)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== IMAGE PREVIEW ==================== */}

      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
      >
        <DialogContent sx={{ p: 0 }}>
          <img
            src={previewImage}
            alt="Preview"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ==================== TOAST ==================== */}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() =>
          setToast({
            ...toast,
            open: false,
          })
        }
      >
        <Alert severity={toast.severity}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsManager;